import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import mktAccountsService from 'src/services/mktAccountsService';
import { CONTAS_SOCIAIS_DESIGN as d } from './contasSociais.design';

const s = d.oauthSelect;

const PROVIDER_META = {
    facebook: {
        labelKey: 'socialAccounts.select.facebookPages',
        hintKey: 'socialAccounts.select.facebookPagesHint',
        icon: 'mdi:facebook',
        iconClass: 'text-[#1877F2]',
    },
    instagram: {
        labelKey: 'socialAccounts.select.instagramAccounts',
        hintKey: 'socialAccounts.select.instagramAccountsHint',
        icon: 'mdi:instagram',
        iconClass: 'text-pink-600',
    },
    meta_ads: {
        labelKey: 'socialAccounts.select.adsAccounts',
        hintKey: 'socialAccounts.select.adsAccountsHint',
        icon: 'mdi:megaphone',
        iconClass: 'text-violet-600',
    },
};

function providerSubtitle(acc, t) {
    if (acc.provider === 'meta_ads') {
        const act = acc.account_info?.act_id || acc.account_id;
        const pages = (acc.account_info?.promote_pages || []).map((p) => p.name).filter(Boolean);
        const pageHint = pages.length ? ` · ${t('socialAccounts.select.pagesPrefix', { pages: pages.join(', ') })}` : '';
        return `${act}${pageHint}`;
    }
    if (acc.provider === 'instagram') {
        return acc.account_info?.username ? `@${acc.account_info.username}` : t('socialAccounts.select.idPrefix', { id: acc.account_id });
    }
    return t('socialAccounts.select.idPrefix', { id: acc.account_id });
}

function SelectableRow({ selected, onToggle, avatarUrl, fallbackIcon, fallbackClass, title, subtitle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className={`${s.row} ${selected ? s.rowSelected : s.rowIdle}`}
        >
            <div className={s.rowAvatar}>
                {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                    <Iconify icon={fallbackIcon} width={20} className={fallbackClass} />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <p className={s.rowTitle}>{title}</p>
                {subtitle && <p className={s.rowSubtitle}>{subtitle}</p>}
            </div>
            <span className={`${s.rowCheck} ${selected ? s.rowCheckOn : s.rowCheckOff}`}>
                {selected && <Iconify icon="eva:checkmark-fill" width={12} />}
            </span>
        </button>
    );
}

/**
 * Tela pos-OAuth: usuario escolhe quais paginas / IG / ad accounts criar
 * como Conta Social, antes de gravar no banco.
 */
export default function MetaAccountSelect({
    clienteId,
    userId,
    sessionId,
    onConnected,
    onCancel,
    preferAds = false,
}) {
    const { t } = useTranslation('settings');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [scopes, setScopes] = useState([]);
    const [selected, setSelected] = useState(() => new Set());
    const [showScopes, setShowScopes] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const res = await mktAccountsService.getOAuthPending(clienteId, sessionId);
                if (cancelled) return;
                const list = res?.data?.accounts || [];
                setAccounts(list);
                setScopes(res?.data?.scopes || []);
                setSelected(new Set(list.map((a) => a.key)));
            } catch (err) {
                if (cancelled) return;
                const msg = (typeof err === 'string' ? err : null)
                    || err?.message
                    || t('socialAccounts.select.loadError');
                setError(msg);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [clienteId, sessionId, t]);

    const groups = useMemo(() => {
        const order = preferAds
            ? ['meta_ads', 'facebook', 'instagram']
            : ['facebook', 'instagram', 'meta_ads'];
        return order
            .map((provider) => ({
                provider,
                meta: PROVIDER_META[provider],
                items: accounts.filter((a) => a.provider === provider),
            }))
            .filter((g) => g.items.length > 0);
    }, [accounts, preferAds]);

    const toggle = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleGroup = (items) => {
        const keys = items.map((i) => i.key);
        const allOn = keys.every((k) => selected.has(k));
        setSelected((prev) => {
            const next = new Set(prev);
            keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
            return next;
        });
    };

    const handleConfirm = async () => {
        if (selected.size === 0) {
            setError(t('socialAccounts.select.selectAtLeastOne'));
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = await mktAccountsService.confirmOAuthPending(clienteId, sessionId, {
                selected_keys: Array.from(selected),
                user_id: userId,
            });
            onConnected?.(res?.data || [], res?.message);
        } catch (err) {
            setError(
                (typeof err === 'string' ? err : null)
                || err?.message
                || t('socialAccounts.select.connectError'),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = async () => {
        try {
            await mktAccountsService.discardOAuthPending(clienteId, sessionId);
        } catch (_) { /* sessao pode ja ter expirado */ }
        onCancel?.();
    };

    if (loading) {
        return (
            <div className={s.page}>
                <div className={`${s.panel} ${s.loading}`}>
                    <Iconify icon="eos-icons:loading" width={28} className="text-[#7C3AED]" />
                    <p className={s.loadingText}>{t('socialAccounts.select.loading')}</p>
                </div>
            </div>
        );
    }

    const scopesPreview = scopes.length > 0
        ? scopes.slice(0, 8).join(', ') + (scopes.length > 8 ? '…' : '')
        : '';

    return (
        <div className={s.page}>
            <div className={s.panel}>
                <div className={s.panelInner}>
                    <header className={s.header}>
                        <div className={s.headerIcon}>
                            <Iconify icon="mdi:facebook" width={26} />
                        </div>
                        <h2 className={s.title}>{t('socialAccounts.select.title')}</h2>
                        <p className={s.subtitle}>{t('socialAccounts.select.subtitle')}</p>
                        {scopes.length > 0 && (
                            <>
                                <button
                                    type="button"
                                    className={s.scopesToggle}
                                    onClick={() => setShowScopes((v) => !v)}
                                >
                                    {showScopes
                                        ? t('socialAccounts.select.hideScopes', 'Ocultar permissões')
                                        : t('socialAccounts.select.showScopes', 'Ver permissões concedidas')}
                                </button>
                                {showScopes && (
                                    <p className={s.scopes}>
                                        {t('socialAccounts.select.grantedScopes', { scopes: scopesPreview })}
                                    </p>
                                )}
                            </>
                        )}
                    </header>

                    {error && (
                        <div className={s.error}>
                            <Iconify icon="eva:alert-circle-outline" width={18} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {groups.length === 0 ? (
                        <div className={s.empty}>
                            <p className={s.emptyTitle}>{t('socialAccounts.select.emptyTitle')}</p>
                            <p className={s.emptyDesc}>{t('socialAccounts.select.emptyDesc')}</p>
                        </div>
                    ) : (
                        groups.map(({ provider, meta, items }) => {
                            const allOn = items.every((i) => selected.has(i.key));
                            return (
                                <section key={provider} className={s.group}>
                                    <div className={s.groupHeader}>
                                        <div className={s.groupIdentity}>
                                            <div className={s.groupIcon}>
                                                <Iconify icon={meta.icon} width={16} className={meta.iconClass} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className={s.groupTitle}>{t(meta.labelKey)}</p>
                                                <p className={s.groupHint}>{t(meta.hintKey)}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={s.groupAction}
                                            onClick={() => toggleGroup(items)}
                                        >
                                            {allOn ? t('socialAccounts.select.clear') : t('socialAccounts.select.selectAll')}
                                        </button>
                                    </div>
                                    <div className={s.list}>
                                        {items.map((acc) => (
                                            <SelectableRow
                                                key={acc.key}
                                                selected={selected.has(acc.key)}
                                                onToggle={() => toggle(acc.key)}
                                                avatarUrl={acc.profile_picture}
                                                fallbackIcon={meta.icon}
                                                fallbackClass={meta.iconClass}
                                                title={acc.account_name || acc.account_id}
                                                subtitle={providerSubtitle(acc, t)}
                                            />
                                        ))}
                                    </div>
                                </section>
                            );
                        })
                    )}

                    <footer className={s.footer}>
                        <button
                            type="button"
                            className={s.cancelBtn}
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            {t('common:buttons.cancel')}
                        </button>
                        <button
                            type="button"
                            className={s.confirmBtn}
                            onClick={handleConfirm}
                            disabled={saving || selected.size === 0}
                        >
                            {saving ? (
                                <span className="inline-flex items-center gap-2">
                                    <Iconify icon="eos-icons:loading" width={16} />
                                    {t('socialAccounts.select.connecting')}
                                </span>
                            ) : (
                                t('socialAccounts.select.connectSelected', { count: selected.size })
                            )}
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    );
}
